import { CircularProgress, Box, Typography } from '@mui/material';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import Legend from '../../shared/Dashboard/legend';

const LayoutComponent = (props) => {
  const { svgMap, loader, blocks, overAllSVG, svgProgressBlockWise, activityId, list, selectBlock = false, setSelectPart, isSelect, type = "CP", selectAll = false, selectPart } = props;
  const wrapperRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const eventHandlersRef = useRef(new Map());
  const navigate = useNavigate();
  const constructionProgressRef = useRef({});
  const selectBlockRef = useRef(selectBlock);
  const blocksRef = useRef(blocks);
  const renderCountRef = useRef(0);
  const [svgReady, setSvgReady] = useState(false);
  const progressUpdatedRef = useRef({});

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    selectBlockRef.current = selectBlock;
  }, [selectBlock]);

  const handleDblClick = useCallback(
    (block) => {
      if (selectBlockRef.current) return;

      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      navigate(`${block?.id}`);
    },
    [navigate]
  );

  const getBlockColor = useCallback((blockId, isSelected) => {
    if (isSelected) {
      return "#FFFF00"; // Yellow for selected blocks
    }
    
    const progress = constructionProgressRef.current[blockId] || 0;
    if (progress === 0) return "#FFFFFF";
    if (progress === 100) return "#B4F8D7";
    return "#FCDDD9";
  }, []);

  const updateBlockVisual = useCallback((blockId, svgId, progressSvgId, isSelected = false) => {
    const blockElement = document.getElementById(svgId);
    if (!blockElement) return;

    // Set the color based on selection state
    const color = getBlockColor(blockId, isSelected);
    blockElement.style.fill = color;

    // Update percentage text
    const progress = constructionProgressRef.current[blockId] || 0;
    let percentageTextElement = null;
    
    // First try to find by progress_svg_id if provided
    if (progressSvgId) {
      percentageTextElement = document.getElementById(progressSvgId);
    }
    
    // If not found, try to find by block-percentage-{blockId} pattern
    if (!percentageTextElement) {
      const cleanBlockId = svgId.replace(/^(path-|block-)?/, '');
      percentageTextElement = document.getElementById(`block-percentage-${cleanBlockId}`);
    }
    
    if (percentageTextElement) {
      const tspanElement = percentageTextElement.querySelector('tspan');
      if (tspanElement) {
        tspanElement.textContent = progress === 0 ? '0%' : `${progress}%`;
      } else {
        percentageTextElement.textContent = progress === 0 ? '0%' : `${progress}%`;
      }
    }
  }, [getBlockColor]);

  const handleBlockClick = useCallback((block) => {
    if (!isSelect) {
      return;
    }

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;

      setSelectPart((prevSelected) => {
        const isAlreadySelected = prevSelected.includes(block.id);

        if (isAlreadySelected) {
          // Deselect the block
          updateBlockVisual(block.id, block.block_svg_id, block.progress_svg_id, false);
          return prevSelected.filter((id) => id !== block.id);
        } else {
          if (type === "QC") {
            // For QC type, only allow single selection
            // First, deselect all previously selected blocks
            prevSelected.forEach((selectedId) => {
              const selectedBlock = blocksRef.current.find(b => b.id === selectedId);
              if (selectedBlock) {
                updateBlockVisual(selectedId, selectedBlock.block_svg_id, selectedBlock.progress_svg_id, false);
              }
            });

            // Select the new block
            updateBlockVisual(block.id, block.block_svg_id, block.progress_svg_id, true);
            return [block.id];
          } else {
            // For other types, allow multiple selection
            updateBlockVisual(block.id, block.block_svg_id, block.progress_svg_id, true);
            return [...prevSelected, block.id];
          }
        }
      });
    }, 250);
  }, [isSelect, setSelectPart, type, updateBlockVisual]);

  const updateProgress = useCallback((progress, id, tempId) => {
    const applyUpdate = () => {
      const blockElement = document.getElementById(id);
      if (!blockElement) {
        setTimeout(applyUpdate, 100);
        return;
      }

      // Find the block ID from the SVG ID
      const block = blocksRef.current.find(b => b.block_svg_id === id);
      const blockId = block ? block.id : null;
      
      // Check if this block is currently selected
      const isSelected = selectPart && selectPart.includes(blockId);
      
      // Only update color if not in select mode or if block is not selected
      if (!selectBlockRef.current || (!isSelected && !selectAll)) {
        const color = selectAll ? "#FFFF00" : (
          progress === 0 ? "#FFFFFF" : 
          progress === 100 ? "#B4F8D7" : 
          "#FCDDD9"
        );
        blockElement.style.fill = color;
      }

      // Update percentage text
      let percentageTextElement = null;
      
      if (tempId) {
        percentageTextElement = document.getElementById(tempId);
      }
      
      if (!percentageTextElement && blockId) {
        const cleanBlockId = id.replace(/^(path-|block-)?/, '');
        percentageTextElement = document.getElementById(`block-percentage-${cleanBlockId}`);
      }
      
      if (percentageTextElement) {
        const tspanElement = percentageTextElement.querySelector('tspan');
        if (tspanElement) {
          tspanElement.textContent = progress === 0 ? '0%' : `${progress}%`;
        } else {
          percentageTextElement.textContent = progress === 0 ? '0%' : `${progress}%`;
        }
      }
    };

    applyUpdate();
  }, [selectAll, selectPart]);

  useEffect(() => {
    // Handle block-wise progress when specific activity is selected
    if (activityId && activityId !== "All" && svgProgressBlockWise && Array.isArray(svgProgressBlockWise)) {
      let newConstructionProgress = {};

      svgProgressBlockWise.forEach((item) => {
        newConstructionProgress[item.block_id] = Math.round(item.progress || 0);
      });

      constructionProgressRef.current = newConstructionProgress;

      // Apply the new progress to existing blocks immediately if SVG is ready
      if (svgReady && blocksRef.current && blocksRef.current.length > 0) {
        blocksRef.current.forEach((block) => {
          if (newConstructionProgress.hasOwnProperty(block.id)) {
            const progress = newConstructionProgress[block.id];
            const isSelected = selectPart && selectPart.includes(block.id);
            if (!isSelected) {
              updateProgress(progress, block.block_svg_id, block.progress_svg_id);
            }
          }
        });
      }
      return;
    }

    // Handle overall SVG progress when "All" is selected or no specific activity
    if (!overAllSVG || !list) return;
    if (activityId && activityId !== "All") return;

    let newConstructionProgress = {};

    Object.keys(overAllSVG).forEach((data) => {
      let subList = [];
      let subWeight = [];

      overAllSVG[data]?.forEach((item) => {
        if (list?.construction?.[item?.activity_id]?.['parent_activity_id']) {
          subWeight.push(list?.construction[item?.activity_id]?.weight || 0);
          subList.push((list?.construction[item?.activity_id]?.weight || 0) * (item?.progress || 0));
        }
      });

      const totalWeight = subWeight.reduce((a, b) => a + b, 0);
      const totalProgress = subList.reduce((a, b) => a + b, 0);

      newConstructionProgress[data] = totalWeight > 0 ? Math.round(totalProgress / totalWeight) : 0;
    });

    constructionProgressRef.current = newConstructionProgress;
  }, [overAllSVG, list, activityId, svgProgressBlockWise, svgReady, updateProgress, selectPart]);

  const addEventListeners = useCallback(() => {
    if (!wrapperRef.current || !blocksRef.current || blocksRef.current.length === 0) return;

    requestAnimationFrame(() => {
      let allElementsFound = true;

      blocksRef.current.forEach((block) => {
        const blockElement = document.getElementById(block.block_svg_id);
        if (!blockElement) {
          allElementsFound = false;
          return;
        }

        // Apply initial colors
        const isSelected = selectPart && selectPart.includes(block.id);
        updateBlockVisual(block.id, block.block_svg_id, block.progress_svg_id, isSelected || selectAll);

        const clickHandler = () => handleBlockClick(block);
        const dblClickHandler = () => handleDblClick(block);

        if (eventHandlersRef.current.has(block.block_svg_id)) {
          const existingHandlers = eventHandlersRef.current.get(block.block_svg_id);
          blockElement.removeEventListener("click", existingHandlers.click);
          blockElement.removeEventListener("dblclick", existingHandlers.dblclick);
        }

        eventHandlersRef.current.set(block.block_svg_id, {
          click: clickHandler,
          dblclick: dblClickHandler,
        });

        blockElement.addEventListener("click", clickHandler);
        blockElement.addEventListener("dblclick", dblClickHandler);
      });

      if (allElementsFound) {
        setSvgReady(true);
      } else {
        setTimeout(addEventListeners, 100);
      }
    });
  }, [handleBlockClick, handleDblClick, updateBlockVisual, selectPart, selectAll]);

  // Handle when selectPart changes
  useEffect(() => {
    if (!blocksRef.current || blocksRef.current.length === 0) return;

    blocksRef.current.forEach((block) => {
      const isSelected = selectPart && selectPart.includes(block.id);
      updateBlockVisual(block.id, block.block_svg_id, block.progress_svg_id, isSelected || selectAll);
    });
  }, [selectPart, selectAll, updateBlockVisual]);

  useEffect(() => {
    if (!svgMap || loader) return;

    const currentEventHandlers = eventHandlersRef.current;

    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          requestAnimationFrame(() => {
            addEventListeners();
          });
          break;
        }
      }
    });

    if (wrapperRef.current) {
      observerRef.current.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id']
      });
    }

    const attemptListeners = () => {
      addEventListeners();

      const retryTimes = [100, 300, 500, 1000, 2000];
      retryTimes.forEach((delay) => {
        setTimeout(() => {
          if (!svgReady) {
            addEventListeners();
          }
        }, delay);
      });
    };

    setTimeout(attemptListeners, 100);

    return () => {
      observerRef.current?.disconnect();

      if (currentEventHandlers) {
        currentEventHandlers.forEach(({ click, dblclick }, id) => {
          const blockElement = document.getElementById(id);
          if (blockElement) {
            blockElement.removeEventListener("click", click);
            blockElement.removeEventListener("dblclick", dblclick);
          }
        });
        currentEventHandlers.clear();
      }
    };
  }, [addEventListeners, svgMap, loader, svgReady]);

  useEffect(() => {
    const currentBlocks = blocksRef.current;
    if (!currentBlocks || currentBlocks.length === 0) return;

    if (selectBlock) {
      currentBlocks.forEach((block) => {
        if (!selectAll) {
          updateBlockVisual(block.id, block.block_svg_id, block.progress_svg_id, false);
        }
      });
    } else {
      if (!selectAll) {
        setSelectPart([]);
      }
    }
  }, [selectBlock, setSelectPart, selectAll, updateBlockVisual]);

  useEffect(() => {
    if (!blocksRef.current || blocksRef.current.length === 0) return;
    if (Object.keys(constructionProgressRef.current).length === 0) return;

    const currentBlocks = blocksRef.current;
    currentBlocks.forEach((block) => {
      if (!progressUpdatedRef.current[block.block_svg_id]) {
        const isSelected = selectPart && selectPart.includes(block.id);
        if (!isSelected && !selectAll) {
          const progress = constructionProgressRef.current[block.id] || 0;
          updateProgress(progress, block.block_svg_id, block.progress_svg_id);
        }
        progressUpdatedRef.current[block.block_svg_id] = true;
      }
    });
  }, [updateProgress, selectPart, selectAll]);

  // Handle selectAll changes
  useEffect(() => {
    if (!blocksRef.current || blocksRef.current.length === 0) return;

    if (selectAll) {
      const allBlockIds = blocksRef.current.map(block => block.id);
      setSelectPart(allBlockIds);
      
      blocksRef.current.forEach((block) => {
        updateBlockVisual(block.id, block.block_svg_id, block.progress_svg_id, true);
      });
    }
  }, [selectAll, setSelectPart, updateBlockVisual]);

  const adjustSvg = useCallback((svg) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = svg;

    const svgElement = tempDiv.querySelector("svg");
    if (svgElement) {
      svgElement.setAttribute("width", "100%");
      svgElement.setAttribute("height", "100%");
      if (!svgElement.hasAttribute("viewBox")) {
        const bbox = svgElement.getBBox();
        svgElement.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
      }

      svgElement.style.backgroundColor = "#FFFFFF";

      const styleTag = document.createElement("style");
      styleTag.textContent = `
        path, polygon, rect { 
          stroke-width: 1px;
        }
      `;
      svgElement.appendChild(styleTag);
    }

    return tempDiv.innerHTML;
  }, []);

  const adjustedSvgMap = useMemo(() => {
    return svgMap ? adjustSvg(svgMap) : '';
  }, [svgMap, adjustSvg]);

  useEffect(() => {
    renderCountRef.current += 1;
  }, []);

  useEffect(() => {
    if (svgReady && blocksRef.current && blocksRef.current.length > 0) {
      const timer = setTimeout(() => {
        blocksRef.current.forEach((block) => {
          const isSelected = selectPart && selectPart.includes(block.id);
          if (!isSelected && !selectAll) {
            const progress = constructionProgressRef.current[block?.id] || 0;
            updateProgress(progress, block?.block_svg_id, block?.progress_svg_id);
          }
        });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [svgReady, updateProgress, selectPart, selectAll]);

  const memoizedContent = useMemo(() => {
    return (
      <Box
        sx={{ display: "flex", cursor: "grab", justifyContent: "center", alignItems: "center" }}
        style={{ width: '100%', height: `calc(100vh - ${type === "Scope Laydown" ? "230px" : "190px"})`, overflow: 'hidden' }}
      >
        {loader ? (
          <CircularProgress />
        ) : (
          <TransformWrapper
            centerZoomedOut
            minScale={0.2}
            centerOnInit
            limitToBounds
            doubleClick={{ disabled: true }}
            onInit={() => {
              setTimeout(() => {
                addEventListeners();
              }, 200);
            }}
          >
            <TransformComponent wrapperStyle={{ height: "100%", width: "100%" }}>
              <div
                id="dashboard-svg"
                ref={wrapperRef}
                className="wrapper-component"
                style={{ width: "100%", height: "100%", cursor: "pointer", position: "relative" }}
                dangerouslySetInnerHTML={{ __html: adjustedSvgMap }}
                onLoad={() => {
                  setTimeout(() => {
                    addEventListeners();
                  }, 200);
                }}
              />
            </TransformComponent>
          </TransformWrapper>
        )}

        {/* Legend / Menu */}
        {isSelect &&
          <Box sx={{ position: "absolute", bottom: type === "logs and checklists" ? 50 : 85, left: "29%" }}>
            <Typography fontSize={16} fontWeight={600} color="#9ca3af">Click on any block to select</Typography>
          </Box>}

        {type === "CP" &&
          <Box
            sx={{ p: 0.5, position: "absolute", bottom: 45, left: "22%", display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 0.5 }}
          >
            <Legend layoutType="SVG" />
          </Box>
        }

        {/* Info */}
        {type !== "Scope Laydown" && <Box
          sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, position: "absolute", bottom: 15, left: "25%", }}
        >
          <InfoOutlined fontSize="small" sx={{ color: '#9ca3af' }} />
          <Typography ml={2} fontSize={16} fontWeight={600} color="#9ca3af">
            Double click on any block to dive in
          </Typography>
        </Box>
        }
      </Box>
    );
  }, [loader, adjustedSvgMap, addEventListeners, type, isSelect]);

  return memoizedContent;
};

export default React.memo(LayoutComponent);